import { useCallback, useEffect, useState } from 'react'
import { Icon, type IconName } from '../components/Icon'
import { Dropdown } from '../components/Dropdown'
import './AdminCategories.css'
import { MAX_NOM } from '../lib/limites'

/**
 * Le catalogue des catégories de départ — décision 2026-08-24 (soir).
 *
 * ⚠️ Ce sont des MODÈLES, jamais les catégories elles-mêmes. Chaque
 * portefeuille reçoit sa copie au premier lancement : renommer « École » ici ne
 * réécrit pas l'historique de ceux qui l'utilisent déjà, et en retirer un ne
 * fait disparaître aucune dépense. L'écran le dit, parce qu'un administrateur
 * qui croit modifier les données de tout le monde n'osera jamais y toucher.
 */

const API = import.meta.env?.VITE_API_URL ?? '/api/v1'

interface Template {
  id: string
  name: string
  direction: 'income' | 'expense'
  icon: string
  position: number
  plan: 'free' | 'premium'
  active: boolean
}

type Brouillon = Omit<Template, 'id' | 'position'> & { id?: string }

const VIDE: Brouillon = {
  name: '', direction: 'expense', icon: 'other', plan: 'free', active: true,
}

export function AdminCategories({ token, onExpired }: {
  token: string
  onExpired: () => void
}) {
  const [rows, setRows] = useState<Template[]>([])
  const [icons, setIcons] = useState<string[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)
  const [occupe, setOccupe] = useState(false)

  const charger = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/category_templates`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.status === 401) return onExpired()
      const payload = await response.json()
      setRows(payload.categories ?? [])
      setIcons(payload.icons ?? [])
      setErreur(null)
    } catch {
      setErreur('Serveur injoignable.')
    }
  }, [token, onExpired])

  // oxlint-disable-next-line react/set-state-in-effect
  useEffect(() => { void charger() }, [charger])

  async function envoyer(methode: string, chemin: string, corps?: unknown) {
    setOccupe(true)
    try {
      const response = await fetch(`${API}/admin/category_templates${chemin}`, {
        method: methode,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: corps === undefined ? undefined : JSON.stringify(corps),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        const details = payload.details as Record<string, string[]> | undefined
        setErreur(details ? Object.entries(details)
          .map(([champ, msgs]) => `${champ} : ${msgs[0]}`).join(' · ')
          : (payload.error ?? 'Refusé.'))
        return false
      }
      setErreur(null)
      await charger()
      return true
    } catch {
      setErreur('Serveur injoignable.')
      return false
    } finally {
      setOccupe(false)
    }
  }

  const parSens = (direction: 'expense' | 'income') =>
    rows.filter((r) => r.direction === direction)

  return (
    <section className="cats">
      <header className="cats-head">
        <div>
          <h2>Catégories de départ</h2>
          {/* Sans cette phrase, un administrateur croit toucher aux données de
              tout le monde, et n'ose plus rien changer. */}
          <p>
            Ce que reçoit un nouveau portefeuille au premier lancement. Modifier
            un modèle ne change rien chez les utilisateurs qui l'ont déjà.
          </p>
        </div>
        {!brouillon && (
          <button type="button" className="cats-add" onClick={() => setBrouillon(VIDE)}>
            <Icon name="plus" size={15} /> Ajouter
          </button>
        )}
      </header>

      {erreur && <p className="fitiavana-error" role="alert">{erreur}</p>}

      {brouillon && (
        <Formulaire
          brouillon={brouillon} icons={icons} occupe={occupe}
          onChange={setBrouillon}
          onCancel={() => setBrouillon(null)}
          onSave={async () => {
            const { id, ...corps } = brouillon
            const ok = id
              ? await envoyer('PATCH', `/${id}`, corps)
              : await envoyer('POST', '', corps)
            if (ok) setBrouillon(null)
          }} />
      )}

      {(['expense', 'income'] as const).map((direction) => (
        <div key={direction} className="cats-group">
          {/* Les dépenses d'abord : l'app est centrée sur ce qui sort. */}
          <h3>{direction === 'expense' ? 'Dépenses' : 'Revenus'}</h3>
          {parSens(direction).length === 0 ? (
            <p className="cats-empty">Aucune catégorie de ce type.</p>
          ) : (
            <ul className="cats-list">
              {parSens(direction).map((row) => (
                <li key={row.id} className={row.active ? '' : 'is-off'}>
                  <span className="cats-icon">
                    <Icon name={row.icon as IconName} size={18} />
                  </span>
                  <span className="cats-name">
                    {row.name}
                    {row.plan === 'premium' && <em>premium</em>}
                    {!row.active && <em className="is-off">retirée</em>}
                  </span>
                  <div className="cats-actions">
                    <button type="button" disabled={occupe}
                            onClick={() => void envoyer('PATCH', `/${row.id}`,
                                                        { active: !row.active })}>
                      {row.active ? 'Retirer' : 'Remettre'}
                    </button>
                    <button type="button" aria-label={`Modifier ${row.name}`} disabled={occupe}
                            onClick={() => setBrouillon({ ...row })}>
                      <Icon name="pencil" size={14} />
                    </button>
                    <button type="button" className="is-danger" disabled={occupe}
                            aria-label={`Supprimer ${row.name}`}
                            onClick={() => void envoyer('DELETE', `/${row.id}`)}>
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </section>
  )
}

function Formulaire(
  { brouillon, icons, occupe, onChange, onCancel, onSave }: {
    brouillon: Brouillon
    icons: string[]
    occupe: boolean
    onChange: (b: Brouillon) => void
    onCancel: () => void
    onSave: () => void
  },
) {
  return (
    <form className="cats-form" onSubmit={(e) => { e.preventDefault(); onSave() }}>
      <label>
        <span>Nom</span>
        <input type="text" required maxLength={MAX_NOM} value={brouillon.name} autoFocus
               onChange={(e) => onChange({ ...brouillon, name: e.target.value })} />
      </label>

      <label>
        <span>Type</span>
        <Dropdown value={brouillon.direction} ariaLabel="Type de catégorie"
                  onChange={(direction) => onChange({ ...brouillon, direction })}
                  options={[
                    { value: 'expense', label: 'Dépense', icon: 'arrowDown' },
                    { value: 'income', label: 'Revenu', icon: 'arrowUp' },
                  ]} />
      </label>

      <label>
        <span>Offre</span>
        <Dropdown value={brouillon.plan} ariaLabel="Offre requise"
                  onChange={(plan) => onChange({ ...brouillon, plan })}
                  options={[
                    { value: 'free', label: 'Gratuite' },
                    { value: 'premium', label: 'Premium' },
                  ]} />
      </label>

      {/* Un choix parmi les icônes que le client SAIT afficher : taper un nom
          libre livrerait à tout le monde une catégorie retombant sur un rond
          gris, sans que personne comprenne pourquoi. */}
      <fieldset className="cats-icons">
        <legend>Icône</legend>
        <div>
          {icons.map((nom) => (
            <button key={nom} type="button"
                    className={brouillon.icon === nom ? 'is-on' : ''}
                    aria-pressed={brouillon.icon === nom} aria-label={nom}
                    onClick={() => onChange({ ...brouillon, icon: nom })}>
              <Icon name={nom as IconName} size={18} />
            </button>
          ))}
        </div>
      </fieldset>

      <div className="cats-form-actions">
        <button type="submit" className="is-primary" disabled={occupe || !brouillon.name.trim()}>
          {occupe ? 'Envoi…' : 'Enregistrer'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}
